/**
 * Auth Service
 *
 * Centralized authentication operations for the application.
 * This service handles all auth-related Supabase interactions.
 *
 * Design Principles:
 * - Pure functions (no React dependencies)
 * - Consistent error handling
 * - Profile caching for offline resilience
 * - Retry logic for network reliability
 */

import { supabase } from '../supabase-config';
import { authHandler } from '../utils/authHandler';

// Cache key for localStorage
const PROFILE_CACHE_KEY = 'cached_user_profile';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

// ============================================================================
// Phone Number Utilities
// ============================================================================

/**
 * Normalize phone number to standard format
 * @param {string} phone - Raw phone number
 * @param {boolean} forSupabase - If true, returns E.164 format with +
 * @returns {string} Normalized phone number
 */
export function normalizePhone(phone, forSupabase = true) {
  let cleaned = phone.replace(/\D/g, '');
  if (!cleaned.startsWith('1') && cleaned.length === 10) {
    cleaned = '1' + cleaned;
  }
  // Supabase Auth expects E.164 format with +, but our DB stores without +
  return forSupabase ? '+' + cleaned : cleaned;
}

// ============================================================================
// Profile Caching
// ============================================================================

/**
 * Cache user profile to localStorage for timeout fallback
 * @param {object} profile - User profile object
 */
export function cacheUserProfile(profile) {
  if (profile?.id && profile?.role) {
    localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify({
      id: profile.id,
      role: profile.role,
      full_name: profile.full_name,
      phone: profile.phone,
      email: profile.email,
      status: profile.status,
      cached_at: Date.now()
    }));
  }
}

/**
 * Get cached profile (valid for 24 hours)
 * @param {string} userId - User ID to verify cache belongs to
 * @returns {object|null} Cached profile or null
 */
export function getCachedProfile(userId) {
  try {
    const cached = localStorage.getItem(PROFILE_CACHE_KEY);
    if (cached) {
      const profile = JSON.parse(cached);
      // Only use cache if it's for the same user and less than 24 hours old
      if (profile.id === userId && (Date.now() - profile.cached_at) < CACHE_TTL_MS) {
        console.log('[authService] Using cached profile for fallback:', profile.full_name, profile.role);
        return profile;
      }
    }
  } catch (e) {
    console.warn('[authService] Failed to read cached profile:', e);
  }
  return null;
}

/**
 * Clear cached profile (call on sign out)
 */
export function clearCachedProfile() {
  localStorage.removeItem(PROFILE_CACHE_KEY);
}

// ============================================================================
// Profile Fetching
// ============================================================================

/**
 * Fetch user profile with retry logic
 * @param {string} userId - User ID
 * @param {number} maxRetries - Maximum retry attempts
 * @param {number} timeoutMs - Timeout per attempt
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function fetchProfileWithRetry(userId, maxRetries = 2, timeoutMs = 8000) {
  // NOTE: We no longer call getSession() here because:
  // 1. When called from onAuthStateChange, the session is already valid
  // 2. Calling getSession() inside callbacks can cause deadlock (gotrue-js #762)
  // 3. The Supabase REST client will use the session automatically

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      console.log(`[authService] Profile fetch attempt ${attempt + 1}/${maxRetries + 1} for user ${userId.slice(0, 8)}...`);

      const startTime = Date.now();
      const profilePromise = supabase
        .from('users')
        .select('id, full_name, phone, email, role, status, username, created_at, updated_at')
        .eq('id', userId)
        .limit(1)
        .then(result => {
          console.log(`[authService] Query completed in ${Date.now() - startTime}ms`);
          if (result.data && result.data.length > 0) {
            return { data: result.data[0], error: null };
          }
          return { data: null, error: result.error || new Error('User not found') };
        });

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Profile fetch timeout')), timeoutMs)
      );

      const { data: profile, error: profileError } = await Promise.race([
        profilePromise,
        timeoutPromise
      ]);

      if (profile) {
        console.log('[authService] Profile fetch successful:', profile.full_name);
        return { data: profile, error: null };
      }

      if (profileError && attempt < maxRetries) {
        console.warn(`[authService] Profile fetch attempt ${attempt + 1} failed:`, profileError.message || profileError);
        await new Promise(r => setTimeout(r, 1000)); // Wait 1s before retry
        continue;
      }

      return { data: null, error: profileError };
    } catch (err) {
      if (attempt < maxRetries) {
        console.warn(`[authService] Profile fetch attempt ${attempt + 1} timed out, retrying...`);
        // On timeout, add extra delay and try to refresh session state
        await new Promise(r => setTimeout(r, 1500));
        // Re-check session to ensure it's still valid
        try {
          await supabase.auth.getSession();
        } catch (e) {
          console.warn('[authService] Session re-check failed');
        }
        continue;
      }
      console.error('[authService] All profile fetch attempts failed');
      return { data: null, error: err };
    }
  }
  return { data: null, error: new Error('All retry attempts failed') };
}

/**
 * Fetch profile by phone number (fallback lookup)
 * @param {string} phone - Phone number (will be normalized)
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function fetchProfileByPhone(phone) {
  const normalizedPhone = normalizePhone(phone, false);
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('phone', normalizedPhone)
    .single();

  return { data, error };
}

// ============================================================================
// User Status Checks
// ============================================================================

/**
 * Check if user account is active
 * @param {object} profile - User profile
 * @returns {boolean}
 */
export function isUserActive(profile) {
  if (!profile) return false;
  // Allow active users, block inactive/suspended
  return profile.status === 'active' || !profile.status; // Default to active if status not set
}

// ============================================================================
// Session Management
// ============================================================================

/**
 * Get current session
 * @returns {Promise<{session: object|null, error: object|null}>}
 */
export async function getSession() {
  const { data: { session }, error } = await supabase.auth.getSession();
  return { session, error };
}

/**
 * Refresh current session if needed
 * @returns {Promise<{session: object|null, error: object|null}>}
 */
export async function refreshSessionIfNeeded() {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();

  if (sessionError || !session) {
    return { session: null, error: sessionError };
  }

  // Check if session is about to expire (within 12 hours)
  const expiresAt = new Date(session.expires_at * 1000);
  const hoursUntilExpiry = (expiresAt - new Date()) / (1000 * 60 * 60);

  if (hoursUntilExpiry < 12) {
    const { data, error } = await supabase.auth.refreshSession();
    if (error) {
      console.error('[authService] Session refresh failed:', error);
      return { session: null, error };
    }
    console.log('[authService] Session refreshed, expires:', new Date(data.session.expires_at * 1000));
    return { session: data.session, error: null };
  }

  return { session, error: null };
}

// ============================================================================
// Sign In Methods
// ============================================================================

/**
 * Sign in with email/password
 * @param {string} username - Username (will be converted to email)
 * @param {string} password - Password
 * @returns {Promise<{success: boolean, profile?: object, error?: string}>}
 */
export async function signInWithPassword(username, password) {
  try {
    // Convert username to actual business email addresses
    const email = username === 'manager'
      ? 'service@worcscale.com'
      : username === 'tech'
        ? 'engineering@worcscale.com'
        : `${username}@worcesterscale.com`; // fallback

    const { data, error } = await supabase.auth.signInWithPassword({
      email: email,
      password: password,
    });

    if (error) {
      return { success: false, error: error.message };
    }

    if (data.user) {
      // Get user profile
      const { data: profile } = await supabase
        .from('users')
        .select('*')
        .eq('id', data.user.id)
        .single();

      if (profile) {
        // Check if user account is active
        if (!isUserActive(profile)) {
          await supabase.auth.signOut();
          return { success: false, error: 'Your account has been deactivated. Please contact your manager.' };
        }

        // Cache successful profile for future fallback
        cacheUserProfile(profile);
        return { success: true, profile };
      }
    }

    return { success: true, profile: null };
  } catch (error) {
    return { success: false, error: 'Login failed. Please try again.' };
  }
}

/**
 * Send OTP to phone number
 * @param {string} phone - Phone number
 * @returns {Promise<{success: boolean, error?: string, rateLimited?: boolean}>}
 */
export async function sendOtp(phone) {
  console.log('[authService] Sending OTP via robust handler');
  const result = await authHandler.sendOtp(phone);

  if (result.success) {
    return { success: true };
  } else {
    return {
      success: false,
      error: result.error,
      rateLimited: result.rateLimited
    };
  }
}

/**
 * Verify OTP code
 * @param {string} phone - Phone number
 * @param {string} code - OTP code
 * @returns {Promise<{success: boolean, method?: string, error?: string, attempts?: number}>}
 */
export async function verifyOtp(phone, code) {
  console.log('[authService] Starting robust verification');

  // Use the multi-strategy verification
  const result = await authHandler.verifyOtp(phone, code);

  if (result.success) {
    console.log(`[authService] Verification successful via ${result.method} (strategy ${result.strategyIndex})`);
    return { success: true, method: result.method };
  } else {
    console.error('[authService] All verification strategies failed');
    return {
      success: false,
      error: result.error || 'Verification failed. Please try again.',
      attempts: result.attempts
    };
  }
}

/**
 * Sign out current user
 * @returns {Promise<void>}
 */
export async function signOut() {
  clearCachedProfile();
  await supabase.auth.signOut();
}

// ============================================================================
// Auth State Subscription
// ============================================================================

/**
 * Subscribe to auth state changes
 * @param {function} callback - Called with (event, session)
 * @returns {object} Subscription object with unsubscribe method
 */
export function onAuthStateChange(callback) {
  const { data: { subscription } } = supabase.auth.onAuthStateChange(callback);
  return subscription;
}

// ============================================================================
// Profile Resolution (combines fetch + cache + RPC fallback)
// ============================================================================

/**
 * Sync user profile via privileged RPC (bypasses RLS)
 * @param {object} session - Supabase session
 * @returns {Promise<{profile: object|null, error: string|null}>}
 */
async function syncProfileViaRpc(session) {
  try {
    console.log('[authService] Syncing profile via RPC...');
    const { data, error } = await supabase.rpc('sync_user_profile_on_login', {
      p_auth_id: session.user.id,
      p_phone: session.user.phone,
      p_email: session.user.email
    });

    if (error) {
      console.error('[authService] RPC error:', error.message);
      return { profile: null, error: error.message };
    }

    if (!data?.success) {
      console.error('[authService] RPC failed:', data?.error);
      return { profile: null, error: data?.error || 'RPC failed' };
    }

    console.log(`[authService] RPC success (${data.action}):`, data.profile.full_name);
    return { profile: data.profile, error: null };
  } catch (err) {
    console.error('[authService] RPC exception:', err.message);
    return { profile: null, error: err.message };
  }
}

/**
 * Resolve user profile with caching and RPC fallback
 * Uses SECURITY DEFINER RPC to bypass RLS for legacy records
 * @param {object} session - Supabase session
 * @returns {Promise<{profile: object|null, source: string}>}
 */
export async function resolveProfile(session) {
  if (!session?.user) {
    console.log('[authService] resolveProfile: No session');
    return { profile: null, source: 'no_session' };
  }

  const userId = session.user.id;
  console.log('[authService] resolveProfile: Checking for user', userId.slice(0, 8) + '...');

  // Try cached profile first for instant response
  // This is critical for performance - ensureUserProfile should have cached the profile
  const cachedProfile = getCachedProfile(userId);
  if (cachedProfile && isUserActive(cachedProfile)) {
    console.log('[authService] resolveProfile: CACHE HIT for', cachedProfile.full_name, cachedProfile.role);
    return { profile: { ...cachedProfile, id: userId }, source: 'cache' };
  }
  console.log('[authService] resolveProfile: Cache miss, syncing via RPC...');

  // Use the privileged RPC to sync/fetch profile (bypasses RLS)
  const { profile, error } = await syncProfileViaRpc(session);

  if (profile) {
    if (!isUserActive(profile)) {
      return { profile: null, source: 'inactive' };
    }
    // Cache the profile for future use
    cacheUserProfile(profile);
    return { profile: { ...profile, id: userId }, source: 'rpc' };
  }

  // RPC failed - this shouldn't happen but log for debugging
  console.error('[authService] Profile resolution failed via RPC:', error);
  return { profile: null, source: 'profile_unverified' };
}

export default {
  normalizePhone,
  cacheUserProfile,
  getCachedProfile,
  clearCachedProfile,
  fetchProfileWithRetry,
  fetchProfileByPhone,
  isUserActive,
  getSession,
  refreshSessionIfNeeded,
  signInWithPassword,
  sendOtp,
  verifyOtp,
  signOut,
  onAuthStateChange,
  resolveProfile
};
